// Passos 1 a 4 do wizard da calculadora de avaliação (CONTRATO-AVM-SITE.md §5).
import { useMemo, useState } from 'react';
import type { AvmConfig } from '../../lib/avm/types';
import type { AvmCatalogoCaracteristicas, AvmErros, AvmForm, AvmOpcao } from './avm.utils';
import {
    AVM_CARACTERISTICAS,
    AVM_CONSERVACOES,
    AVM_CONSERVACOES_7,
    AVM_MAX_CARACTERISTICAS,
    iconeDoTipo,
    mascaraArea,
    mascaraCep,
    mascaraMoeda,
    mascaraTelefone,
    pedeAreaTerreno,
    pedeElevador,
    prepararTipos,
} from './avm.utils';
import { AvmCombo, Campo, Chip, Icone, Stepper, Toggle } from './AvmUI';
import styles from './AvmCalculator.module.css';

export interface PassoBaseProps {
    form: AvmForm;
    erros: AvmErros;
    travado: boolean;
    idBase: string;
    setCampo: <K extends keyof AvmForm>(_campo: K, _valor: AvmForm[K]) => void;
    aoSairDoCampo: (_campo: keyof AvmForm) => void;
}

const ICONES_TIPO = {
    apartamento: 'apartamento',
    casa: 'casa',
    terreno: 'terreno',
    rural: 'rural',
    galpao: 'galpao',
    comercial: 'comercial',
    imovel: 'imovel',
} as const;

/* ── 1. Onde fica o imóvel? ────────────────────────────────────────────────── */

export const Passo1 = ({
    form,
    erros,
    travado,
    idBase,
    setCampo,
    aoSairDoCampo,
    cidades,
    bairros,
    carregandoBairros,
}: PassoBaseProps & {
    cidades: AvmOpcao[];
    bairros: AvmOpcao[];
    carregandoBairros: boolean;
}) => {
    const temListaDeBairros = bairros.length > 0;

    return (
        <div className={styles.campos}>
            <Campo id={`${idBase}-cidade`} rotulo="Cidade" erro={erros.cidadeId}>
                <AvmCombo
                    id={`${idBase}-cidade`}
                    texto={form.cidadeNome}
                    valorId={form.cidadeId}
                    opcoes={cidades}
                    placeholder="Comece a digitar a cidade"
                    disabled={travado}
                    invalido={!!erros.cidadeId}
                    aoMudar={(opcao, texto) => {
                        setCampo('cidadeNome', texto);
                        setCampo('cidadeId', opcao ? String(opcao.id) : '');
                        // trocou de cidade: o bairro anterior não vale mais
                        setCampo('bairroId', '');
                        setCampo('bairroNome', '');
                    }}
                    aoSair={() => aoSairDoCampo('cidadeId')}
                />
            </Campo>

            <Campo
                id={`${idBase}-bairro`}
                rotulo="Bairro"
                erro={erros.bairroNome}
                ajuda={
                    carregandoBairros
                        ? 'Carregando os bairros da cidade…'
                        : 'O bairro deixa a estimativa bem mais precisa.'
                }
            >
                <AvmCombo
                    id={`${idBase}-bairro`}
                    texto={form.bairroNome}
                    valorId={form.bairroId}
                    opcoes={bairros}
                    // Sem lista de bairros para a cidade (o site não tem imóvel lá ainda),
                    // o campo vira texto livre — melhor que travar o visitante.
                    permitirLivre={!temListaDeBairros}
                    placeholder={form.cidadeId ? 'Bairro do imóvel' : 'Escolha a cidade primeiro'}
                    disabled={travado || !form.cidadeId}
                    invalido={!!erros.bairroNome}
                    aoMudar={(opcao, texto) => {
                        setCampo('bairroNome', texto);
                        setCampo('bairroId', opcao ? String(opcao.id) : '');
                    }}
                    aoSair={() => aoSairDoCampo('bairroNome')}
                />
            </Campo>

            <div className={`${styles.linha} ${styles.linhaDupla}`}>
                <Campo id={`${idBase}-endereco`} rotulo="Rua" erro={erros.endereco}>
                    <input
                        id={`${idBase}-endereco`}
                        name="endereco"
                        type="text"
                        className={`${styles.input} ${erros.endereco ? styles.inputErro : ''}`}
                        value={form.endereco}
                        disabled={travado}
                        autoComplete="address-line1"
                        placeholder="Nome da rua"
                        aria-invalid={!!erros.endereco}
                        aria-describedby={erros.endereco ? `${idBase}-endereco-erro` : undefined}
                        onChange={(e) => setCampo('endereco', e.target.value)}
                        onBlur={() => aoSairDoCampo('endereco')}
                    />
                </Campo>
                <Campo id={`${idBase}-numero`} rotulo="Número" erro={erros.numero}>
                    <input
                        id={`${idBase}-numero`}
                        name="numero"
                        type="text"
                        inputMode="numeric"
                        className={`${styles.input} ${erros.numero ? styles.inputErro : ''}`}
                        value={form.numero}
                        disabled={travado}
                        placeholder="000"
                        aria-invalid={!!erros.numero}
                        aria-describedby={erros.numero ? `${idBase}-numero-erro` : undefined}
                        onChange={(e) => setCampo('numero', e.target.value.slice(0, 20))}
                        onBlur={() => aoSairDoCampo('numero')}
                    />
                </Campo>
            </div>

            <Campo id={`${idBase}-cep`} rotulo="CEP" opcional erro={erros.cep}>
                <input
                    id={`${idBase}-cep`}
                    name="cep"
                    type="text"
                    inputMode="numeric"
                    className={`${styles.input} ${erros.cep ? styles.inputErro : ''}`}
                    value={form.cep}
                    disabled={travado}
                    autoComplete="postal-code"
                    placeholder="00000-000"
                    aria-invalid={!!erros.cep}
                    aria-describedby={erros.cep ? `${idBase}-cep-erro` : undefined}
                    onChange={(e) => setCampo('cep', mascaraCep(e.target.value))}
                    onBlur={() => aoSairDoCampo('cep')}
                />
            </Campo>

            <Campo id={`${idBase}-complemento`} rotulo="Complemento" opcional>
                <input
                    id={`${idBase}-complemento`}
                    name="complemento"
                    type="text"
                    className={styles.input}
                    value={form.complemento}
                    disabled={travado}
                    placeholder="Apto, bloco, torre…"
                    onChange={(e) => setCampo('complemento', e.target.value.slice(0, 60))}
                />
            </Campo>
        </div>
    );
};

/* ── 2. Como é o imóvel? ───────────────────────────────────────────────────── */

export const Passo2 = ({
    form,
    erros,
    travado,
    idBase,
    setCampo,
    aoSairDoCampo,
    tipos,
}: PassoBaseProps & { tipos: AvmOpcao[] }) => {
    const mostrarTerreno = pedeAreaTerreno(form.tipoNome);
    const mostrarElevador = pedeElevador(form.tipoNome);

    // O tenant cadastra os tipos à mão: vem repetido ("Apartamento" 2x) e vem MUITO
    // (26 linhas no cs1722). `prepararTipos` tira os repetidos e, na lista grande,
    // separa os poucos que viram card do resto, que vai pro combo abaixo.
    const listaTipos = useMemo(() => prepararTipos(tipos), [tipos]);
    const idsDosCards = useMemo(() => {
        const mapa: Record<string, boolean> = {};
        listaTipos.cards.forEach((tipo) => {
            mapa[String(tipo.id)] = true;
        });
        return mapa;
    }, [listaTipos]);

    // Estado único: um `tipoId`. Se o escolhido está nos cards, o combo aparece vazio
    // (e vice-versa) — escolher de um lado limpa o outro sem guardar dois estados.
    const escolhidoNoCard = !!form.tipoId && !!idsDosCards[form.tipoId];
    const textoOutroTipo = escolhidoNoCard ? '' : form.tipoNome;
    const idOutroTipo = escolhidoNoCard ? '' : form.tipoId;

    return (
        <div className={styles.campos}>
            <div className={styles.campo}>
                <span className={styles.rotulo} id={`${idBase}-tipo-rotulo`}>
                    Tipo do imóvel
                </span>
                <div
                    id={`${idBase}-tipoId-grupo`}
                    className={styles.gradeTipos}
                    role="radiogroup"
                    aria-labelledby={`${idBase}-tipo-rotulo`}
                    aria-describedby={erros.tipoId ? `${idBase}-tipoId-erro` : undefined}
                >
                    {listaTipos.cards.map((tipo) => {
                        const ativo = String(tipo.id) === form.tipoId;
                        const icone = iconeDoTipo(tipo.nome) as keyof typeof ICONES_TIPO;
                        return (
                            <button
                                key={tipo.id}
                                type="button"
                                role="radio"
                                // nome acessível = só o nome do tipo (o ícone é aria-hidden):
                                // é por ele que o teste e o leitor de tela acham o card.
                                aria-label={tipo.nome}
                                aria-checked={ativo}
                                disabled={travado}
                                className={`${styles.cardTipo} ${ativo ? styles.cardTipoAtivo : ''}`}
                                onClick={() => {
                                    setCampo('tipoId', String(tipo.id));
                                    setCampo('tipoNome', tipo.nome);
                                }}
                            >
                                <span className={styles.cardTipoIcone}>
                                    <Icone nome={ICONES_TIPO[icone]} tamanho={26} />
                                </span>
                                <span className={styles.cardTipoNome}>{tipo.nome}</span>
                            </button>
                        );
                    })}
                </div>

                {listaTipos.usaCombo ? (
                    <div className={styles.tipoOutro}>
                        <label className={styles.rotuloSecundario} htmlFor={`${idBase}-tipoOutro`}>
                            Outro tipo de imóvel
                        </label>
                        <AvmCombo
                            id={`${idBase}-tipoOutro`}
                            texto={textoOutroTipo}
                            valorId={idOutroTipo}
                            opcoes={listaTipos.todos}
                            placeholder="Ver a lista completa"
                            disabled={travado}
                            invalido={!!erros.tipoId}
                            aoMudar={(opcao, texto) => {
                                setCampo('tipoNome', opcao ? opcao.nome : texto);
                                setCampo('tipoId', opcao ? String(opcao.id) : '');
                            }}
                            aoSair={() => aoSairDoCampo('tipoId')}
                        />
                    </div>
                ) : null}

                {erros.tipoId ? (
                    <span className={styles.erro} id={`${idBase}-tipoId-erro`} role="alert">
                        <Icone nome="alerta" tamanho={14} trecho={2} />
                        {erros.tipoId}
                    </span>
                ) : null}
            </div>

            <div className={`${styles.linha} ${mostrarTerreno ? styles.linhaDuasIguais : ''}`}>
                <Campo id={`${idBase}-areaPrivativa`} rotulo="Área útil (m²)" erro={erros.areaPrivativa}>
                    <span className={`${styles.comAdorno} ${styles.comAdornoDireita}`}>
                        <input
                            id={`${idBase}-areaPrivativa`}
                            name="areaPrivativa"
                            type="text"
                            inputMode="decimal"
                            className={`${styles.input} ${erros.areaPrivativa ? styles.inputErro : ''}`}
                            value={form.areaPrivativa}
                            disabled={travado}
                            placeholder="Ex.: 70"
                            aria-invalid={erros.areaPrivativa ? true : undefined}
                            aria-describedby={erros.areaPrivativa ? `${idBase}-areaPrivativa-erro` : undefined}
                            onChange={(e) => setCampo('areaPrivativa', mascaraArea(e.target.value))}
                            onBlur={() => aoSairDoCampo('areaPrivativa')}
                        />
                        <span className={`${styles.adorno} ${styles.adornoDireita}`}>m²</span>
                    </span>
                </Campo>

                {mostrarTerreno ? (
                    <Campo id={`${idBase}-areaTerreno`} rotulo="Área do terreno (m²)" opcional erro={erros.areaTerreno}>
                        <span className={`${styles.comAdorno} ${styles.comAdornoDireita}`}>
                            <input
                                id={`${idBase}-areaTerreno`}
                                name="areaTerreno"
                                type="text"
                                inputMode="decimal"
                                className={`${styles.input} ${erros.areaTerreno ? styles.inputErro : ''}`}
                                value={form.areaTerreno}
                                disabled={travado}
                                placeholder="Ex.: 360"
                                aria-invalid={erros.areaTerreno ? true : undefined}
                                aria-describedby={erros.areaTerreno ? `${idBase}-areaTerreno-erro` : undefined}
                                onChange={(e) => setCampo('areaTerreno', mascaraArea(e.target.value))}
                                onBlur={() => aoSairDoCampo('areaTerreno')}
                            />
                            <span className={`${styles.adorno} ${styles.adornoDireita}`}>m²</span>
                        </span>
                    </Campo>
                ) : null}
            </div>

            <div className={styles.steppers}>
                <Stepper
                    rotulo="Quartos"
                    valor={form.dormitorios}
                    disabled={travado}
                    aoMudar={(v) => setCampo('dormitorios', v)}
                />
                <Stepper rotulo="Suítes" valor={form.suites} disabled={travado} aoMudar={(v) => setCampo('suites', v)} />
                <Stepper
                    rotulo="Banheiros"
                    valor={form.banheiros}
                    disabled={travado}
                    aoMudar={(v) => setCampo('banheiros', v)}
                />
                <Stepper rotulo="Vagas" valor={form.vagas} disabled={travado} aoMudar={(v) => setCampo('vagas', v)} />
            </div>

            {mostrarElevador ? (
                <Toggle
                    rotulo="Tem elevador?"
                    ativo={form.elevador}
                    disabled={travado}
                    aoMudar={(v) => setCampo('elevador', v)}
                />
            ) : null}

            <Campo id={`${idBase}-anoConstrucao`} rotulo="Ano de construção" opcional erro={erros.anoConstrucao}>
                <input
                    id={`${idBase}-anoConstrucao`}
                    name="anoConstrucao"
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    className={`${styles.input} ${erros.anoConstrucao ? styles.inputErro : ''}`}
                    value={form.anoConstrucao}
                    disabled={travado}
                    placeholder="Ex.: 2015"
                    aria-invalid={erros.anoConstrucao ? true : undefined}
                    aria-describedby={erros.anoConstrucao ? `${idBase}-anoConstrucao-erro` : undefined}
                    onChange={(e) => setCampo('anoConstrucao', e.target.value.replace(/\D/g, '').slice(0, 4))}
                    onBlur={() => aoSairDoCampo('anoConstrucao')}
                />
            </Campo>
        </div>
    );
};

/* ── 3. Diferenciais + conservação ─────────────────────────────────────────── */

const normalizarBusca = (v: string) =>
    String(v || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();

/** Lista fixa de sempre (os sites sem o catálogo do CRM ligado). */
const CaracteristicasFixas = ({ form, travado, idBase, setCampo }: PassoBaseProps) => (
    <div className={styles.campo}>
        <span className={styles.rotulo} id={`${idBase}-carac-rotulo`}>
            O imóvel tem…
            <span className={styles.opcional}>(pode marcar vários)</span>
        </span>
        <div className={styles.chips} role="group" aria-labelledby={`${idBase}-carac-rotulo`}>
            {AVM_CARACTERISTICAS.map((item) => {
                const ativo = form.caracteristicas.indexOf(item) >= 0;
                return (
                    <Chip
                        key={item}
                        rotulo={item.charAt(0).toUpperCase() + item.slice(1)}
                        ativo={ativo}
                        disabled={travado}
                        aoMudar={(marcado) =>
                            setCampo(
                                'caracteristicas',
                                marcado
                                    ? form.caracteristicas.concat([item])
                                    : form.caracteristicas.filter((c) => c !== item),
                            )
                        }
                    />
                );
            })}
        </div>
    </div>
);

/** Chip da grade do catálogo: mesmo tamanho para todos, caixinha de marcação e nome com "…". */
const ChipCatalogo = ({
    nome,
    ativo,
    disabled,
    aoMudar,
}: {
    nome: string;
    ativo: boolean;
    disabled?: boolean;
    aoMudar: (_v: boolean) => void;
}) => (
    <button
        type="button"
        className={`${styles.caracChip} ${ativo ? styles.caracChipAtivo : ''}`}
        role="checkbox"
        aria-checked={ativo}
        disabled={disabled}
        onClick={() => aoMudar(!ativo)}
    >
        <span className={styles.caracCheck} aria-hidden="true">
            {ativo ? <Icone nome="check" tamanho={14} trecho={2.8} /> : null}
        </span>
        {/* O nome corta com "…"; o title devolve inteiro no hover, sem mexer na grade. */}
        <span className={styles.caracNome} title={nome}>
            {nome}
        </span>
    </button>
);

/**
 * Catálogo do CRM em dois blocos — unidade e condomínio — como no passo "O que o imóvel tem"
 * da calculadora do CRM (PassoCaracteristicas): busca, contador e grade de 3 colunas. Cada bloco
 * abre e fecha no cabeçalho; começam fechados e a busca abre os que têm resultado.
 */
const CaracteristicasCatalogo = ({
    form,
    travado,
    idBase,
    setCampo,
    catalogo,
}: PassoBaseProps & { catalogo: AvmCatalogoCaracteristicas }) => {
    const [filtro, setFiltro] = useState('');
    const [abertos, setAbertos] = useState<Record<string, boolean>>({});
    const q = normalizarBusca(filtro);
    const ids = form.caracteristicaIds;
    const noLimite = ids.length >= AVM_MAX_CARACTERISTICAS;

    const grupos = [
        { chave: 'unidade', titulo: 'Características da unidade', todas: catalogo.unidade },
        { chave: 'condominio', titulo: 'Características do condomínio', todas: catalogo.condominio },
    ].filter((g) => g.todas.length > 0);

    const alternar = (id: number, marcado: boolean) =>
        setCampo(
            'caracteristicaIds',
            marcado ? (ids.indexOf(id) >= 0 ? ids : ids.concat([id])) : ids.filter((x) => x !== id),
        );

    return (
        <div className={styles.campo}>
            <span className={styles.rotulo} id={`${idBase}-carac-rotulo`}>
                O imóvel tem…
                <span className={styles.opcional}>(pode marcar vários)</span>
            </span>
            <div className={styles.caracTopo}>
                <input
                    id={`${idBase}-carac-busca`}
                    className={styles.input}
                    type="search"
                    value={filtro}
                    disabled={travado}
                    autoComplete="off"
                    placeholder="Buscar característica (ex.: piscina, portaria)"
                    aria-label="Buscar característica"
                    onChange={(e) => setFiltro(e.target.value)}
                    onKeyDown={(e) => {
                        // Enter aqui filtra — não avança o passo (o form trata Enter como "Continuar")
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                        }
                    }}
                />
                <span className={styles.caracContador} aria-live="polite">
                    <strong>{ids.length}</strong> {ids.length === 1 ? 'marcada' : 'marcadas'}
                </span>
            </div>
            {grupos.map((g) => {
                const lista = q ? g.todas.filter((c) => normalizarBusca(c.nome).indexOf(q) >= 0) : g.todas;
                // com busca, abre sozinho o bloco que tem resultado (e fecha o que não tem)
                const aberto = q ? lista.length > 0 : !!abertos[g.chave];
                const marcadasNoGrupo = g.todas.filter((c) => ids.indexOf(c.id) >= 0).length;
                const corpoId = `${idBase}-carac-${g.chave}-corpo`;
                return (
                    <section key={g.chave} className={styles.caracGrupo}>
                        <button
                            type="button"
                            className={styles.caracCabecalho}
                            aria-expanded={aberto}
                            aria-controls={corpoId}
                            onClick={() => setAbertos((a) => ({ ...a, [g.chave]: !aberto }))}
                        >
                            <span className={styles.caracTitulo}>
                                {g.titulo} ({g.todas.length})
                                {marcadasNoGrupo ? (
                                    <span className={styles.caracMarcadas}>
                                        {marcadasNoGrupo} {marcadasNoGrupo === 1 ? 'marcada' : 'marcadas'}
                                    </span>
                                ) : null}
                            </span>
                            <span className={`${styles.caracSeta} ${aberto ? styles.caracSetaAberta : ''}`}>
                                <Icone nome="seta" tamanho={18} trecho={2.2} />
                            </span>
                        </button>
                        {aberto ? (
                            <div id={corpoId}>
                                {lista.length === 0 ? (
                                    <span className={styles.ajuda}>Nenhuma característica com “{filtro.trim()}” aqui.</span>
                                ) : (
                                    <div className={styles.caracGrade} role="group" aria-label={g.titulo}>
                                        {lista.map((c) => {
                                            const ativo = ids.indexOf(c.id) >= 0;
                                            return (
                                                <ChipCatalogo
                                                    key={c.id}
                                                    nome={c.nome}
                                                    ativo={ativo}
                                                    disabled={travado || (!ativo && noLimite)}
                                                    aoMudar={(marcado) => alternar(c.id, marcado)}
                                                />
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        ) : null}
                    </section>
                );
            })}
            {noLimite ? (
                <span className={styles.ajuda}>Você marcou o máximo de {AVM_MAX_CARACTERISTICAS} características.</span>
            ) : null}
        </div>
    );
};

/** Autorização (caixinha + texto do config). Passo 4 por padrão; passo 3 nos sites com gate. */
const Consentimento = ({
    form,
    erros,
    travado,
    idBase,
    setCampo,
    texto,
}: Pick<PassoBaseProps, 'form' | 'erros' | 'travado' | 'idBase' | 'setCampo'> & { texto: string }) => (
    <div>
        <label className={`${styles.consent} ${erros.consentimento ? styles.consentErro : ''}`}>
            <input
                id={`${idBase}-consentimento`}
                name="consentimento"
                type="checkbox"
                className={styles.checkbox}
                checked={form.consentimento}
                disabled={travado}
                aria-invalid={erros.consentimento ? true : undefined}
                aria-describedby={erros.consentimento ? `${idBase}-consentimento-erro` : undefined}
                onChange={(e) => setCampo('consentimento', e.target.checked)}
            />
            <span>{texto}</span>
        </label>
        {erros.consentimento ? (
            <span className={styles.erro} id={`${idBase}-consentimento-erro`} role="alert">
                <Icone nome="alerta" tamanho={14} trecho={2} />
                {erros.consentimento}
            </span>
        ) : null}
    </div>
);

export const Passo3 = ({
    catalogo,
    carregandoCatalogo,
    comConservacao7,
    consentNoPasso3,
    config,
    ...props
}: PassoBaseProps & {
    catalogo?: AvmCatalogoCaracteristicas | null;
    carregandoCatalogo?: boolean;
    /** cs2514 (21/09): 7 opções sem pré-marcação, escolha obrigatória — ver `usaConservacao7Opcoes`. */
    comConservacao7?: boolean;
    consentNoPasso3?: boolean;
    config?: AvmConfig;
}) => {
    const { form, erros, travado, idBase, setCampo } = props;
    const opcoesConservacao = comConservacao7 ? AVM_CONSERVACOES_7 : AVM_CONSERVACOES;
    return (
        <div className={styles.campos}>
            {carregandoCatalogo ? (
                <div className={styles.campo}>
                    <span className={styles.rotulo}>O imóvel tem…</span>
                    <span className={styles.ajuda} aria-live="polite">Carregando as características…</span>
                </div>
            ) : catalogo ? (
                <CaracteristicasCatalogo {...props} catalogo={catalogo} />
            ) : (
                <CaracteristicasFixas {...props} />
            )}

            <div className={styles.campo}>
                <span className={styles.rotulo} id={`${idBase}-conserv-rotulo`}>
                    Estado de conservação
                </span>
                <div
                    id={`${idBase}-estadoConservacao-grupo`}
                    className={styles.gradeConservacao}
                    role="radiogroup"
                    aria-labelledby={`${idBase}-conserv-rotulo`}
                >
                    {opcoesConservacao.map((opcao) => {
                        const ativo = form.estadoConservacao === opcao.valor;
                        return (
                            <button
                                key={opcao.valor}
                                type="button"
                                role="radio"
                                aria-checked={ativo}
                                disabled={travado}
                                className={`${styles.conservacao} ${ativo ? styles.conservacaoAtiva : ''}`}
                                onClick={() => setCampo('estadoConservacao', opcao.valor)}
                            >
                                <span className={styles.conservacaoRotulo}>{opcao.rotulo}</span>
                                <span className={styles.conservacaoAjuda}>{opcao.ajuda}</span>
                            </button>
                        );
                    })}
                </div>
                {erros.estadoConservacao ? (
                    <span className={styles.erro} id={`${idBase}-estadoConservacao-erro`} role="alert">
                        <Icone nome="alerta" tamanho={14} trecho={2} />
                        {erros.estadoConservacao}
                    </span>
                ) : null}
            </div>

            {/* cs2514: a autorização fica aqui, logo abaixo do "Estado de conservação" */}
            {consentNoPasso3 && config ? (
                <Consentimento form={form} erros={erros} travado={travado} idBase={idBase} setCampo={setCampo} texto={config.consentText} />
            ) : null}
        </div>
    );
};

/* ── 4. Quer anunciar? + contato ───────────────────────────────────────────── */

export const Passo4 = ({
    form,
    erros,
    travado,
    idBase,
    setCampo,
    aoSairDoCampo,
    config,
    consentNoPasso3,
}: PassoBaseProps & { config: AvmConfig; consentNoPasso3?: boolean }) => (
    <div className={styles.campos}>
        <div className={styles.campo}>
            <span className={styles.rotulo} id={`${idBase}-intencao-rotulo`}>
                {config.perguntaAnunciar}
            </span>
            <div
                id={`${idBase}-intencao-grupo`}
                className={styles.gradeIntencao}
                role="radiogroup"
                aria-labelledby={`${idBase}-intencao-rotulo`}
            >
                {[
                    {
                        valor: 'anunciar' as const,
                        titulo: config.opcaoAnunciarSim,
                        ajuda: 'Um corretor entra em contato para combinar a visita e o anúncio.',
                    },
                    {
                        valor: 'avaliar_profissional' as const,
                        titulo: config.opcaoAnunciarNao,
                        ajuda: 'Você recebe a estimativa e decide depois, sem compromisso.',
                    },
                ].map((opcao) => {
                    const ativo = form.intencao === opcao.valor;
                    return (
                        <button
                            key={opcao.valor}
                            type="button"
                            role="radio"
                            aria-checked={ativo}
                            disabled={travado}
                            className={`${styles.cardIntencao} ${ativo ? styles.cardIntencaoAtivo : ''}`}
                            onClick={() => setCampo('intencao', opcao.valor)}
                        >
                            <span className={styles.cardIntencaoMarca}>
                                {ativo ? <Icone nome="check" tamanho={12} trecho={3} /> : null}
                            </span>
                            <span className={styles.cardIntencaoTexto}>
                                <span className={styles.cardIntencaoTitulo}>{opcao.titulo}</span>
                                <span className={styles.cardIntencaoAjuda}>{opcao.ajuda}</span>
                            </span>
                        </button>
                    );
                })}
            </div>
            {erros.intencao ? (
                <span className={styles.erro} id={`${idBase}-intencao-erro`} role="alert">
                    <Icone nome="alerta" tamanho={14} trecho={2} />
                    {erros.intencao}
                </span>
            ) : null}
        </div>

        <Campo id={`${idBase}-nome`} rotulo="Seu nome" erro={erros.nome}>
            <input
                id={`${idBase}-nome`}
                name="nome"
                type="text"
                className={`${styles.input} ${erros.nome ? styles.inputErro : ''}`}
                value={form.nome}
                disabled={travado}
                autoComplete="name"
                placeholder="Como podemos te chamar?"
                aria-invalid={erros.nome ? true : undefined}
                aria-describedby={erros.nome ? `${idBase}-nome-erro` : undefined}
                onChange={(e) => setCampo('nome', e.target.value.slice(0, 120))}
                onBlur={() => aoSairDoCampo('nome')}
            />
        </Campo>

        <div className={`${styles.linha} ${styles.linhaDuasIguais}`}>
            <Campo id={`${idBase}-telefone`} rotulo="WhatsApp" erro={erros.telefone}>
                <input
                    id={`${idBase}-telefone`}
                    name="telefone"
                    type="tel"
                    inputMode="tel"
                    className={`${styles.input} ${erros.telefone ? styles.inputErro : ''}`}
                    value={form.telefone}
                    disabled={travado}
                    autoComplete="tel"
                    placeholder="(00) 00000-0000"
                    aria-invalid={erros.telefone ? true : undefined}
                    aria-describedby={erros.telefone ? `${idBase}-telefone-erro` : undefined}
                    onChange={(e) => setCampo('telefone', mascaraTelefone(e.target.value))}
                    onBlur={() => aoSairDoCampo('telefone')}
                />
            </Campo>
            <Campo id={`${idBase}-precoDesejado`} rotulo="Quanto você acha que vale?" opcional>
                <span className={styles.comAdorno}>
                    <span className={styles.adorno}>R$</span>
                    <input
                        id={`${idBase}-precoDesejado`}
                        name="precoDesejado"
                        type="text"
                        inputMode="numeric"
                        className={styles.input}
                        value={form.precoDesejado}
                        disabled={travado}
                        placeholder="500.000"
                        onChange={(e) => setCampo('precoDesejado', mascaraMoeda(e.target.value))}
                    />
                </span>
            </Campo>
        </div>

        <Campo id={`${idBase}-email`} rotulo="E-mail" opcional erro={erros.email}>
            <input
                id={`${idBase}-email`}
                name="email"
                type="email"
                className={`${styles.input} ${erros.email ? styles.inputErro : ''}`}
                value={form.email}
                disabled={travado}
                autoComplete="email"
                placeholder="voce@email.com"
                aria-invalid={erros.email ? true : undefined}
                aria-describedby={erros.email ? `${idBase}-email-erro` : undefined}
                onChange={(e) => setCampo('email', e.target.value.slice(0, 120))}
                onBlur={() => aoSairDoCampo('email')}
            />
        </Campo>

        {consentNoPasso3 ? null : <Consentimento form={form} erros={erros} travado={travado} idBase={idBase} setCampo={setCampo} texto={config.consentText} />}
    </div>
);
